import { ChevronRight, Home } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  Typography,
  alpha,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import FileGrid, { encodeKey, isDirectory } from "./FileGrid";
import MultiSelectToolbar from "./MultiSelectToolbar";
import UploadDrawer, { UploadFab } from "./UploadDrawer";
import { copyPaste, fetchPath } from "./app/transfer";
import { useTransferQueue, useUploadEnqueue } from "./app/transferQueue";
import { authenticatedFetch } from "./app/auth";

interface CenteredProps {
  children: React.ReactNode;
}

function Centered({ children }: CenteredProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        gap: 2,
      }}
    >
      {children}
    </Box>
  );
}

interface PathBreadcrumbProps {
  path: string;
  onCwdChange: (path: string) => void;
}

function PathBreadcrumb({ path, onCwdChange }: PathBreadcrumbProps) {
  const parts = path.replace(/\/$/, "").split("/");

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Breadcrumbs
        separator={<ChevronRight sx={{ fontSize: 18, color: 'text.secondary' }} />}
        sx={{
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
          },
          '& .MuiBreadcrumbs-li': {
            maxWidth: { xs: 120, sm: 200 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Button
          onClick={() => onCwdChange("")}
          startIcon={<Home sx={{ fontSize: 18 }} />}
          size="small"
          sx={{
            minWidth: 0,
            padding: '4px 8px',
            color: 'text.secondary',
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: 'rgba(60,64,67,0.08)',
            },
          }}
        >
          <Typography sx={{ display: { xs: 'none', sm: 'inline' }, fontSize: '0.875rem' }}>
            My Drive
          </Typography>
        </Button>
        {parts.map((part, index) =>
          index === parts.length - 1 ? (
            <Typography
              key={index}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.875rem',
                color: 'text.primary',
                fontWeight: 400,
              }}
            >
              {part}
            </Typography>
          ) : (
            <Link
              key={index}
              component="button"
              underline="none"
              color="text.secondary"
              onClick={() => {
                onCwdChange(parts.slice(0, index + 1).join("/") + "/");
              }}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.875rem',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'text.primary',
                },
              }}
            >
              {part}
            </Link>
          )
        )}
      </Breadcrumbs>
    </Box>
  );
}

interface DropZoneProps {
  children: React.ReactNode;
  onDrop: (files: FileList) => void;
}

function DropZone({ children, onDrop }: DropZoneProps) {
  const [dragging, setDragging] = useState(false);

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflowY: "auto",
        backgroundColor: 'background.default',
        position: 'relative',
      }}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={(event) => {
        if (event.currentTarget === event.target) {
          setDragging(false);
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(event.dataTransfer.files);
        setDragging(false);
      }}
    >
      {dragging && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            backgroundColor: 'rgba(26, 115, 232, 0.08)',
            border: '3px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            m: 2,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              backgroundColor: 'background.paper',
              p: 4,
              borderRadius: 2,
              boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)',
            }}
          >
            <Typography variant="h6" color="primary" fontWeight={500} gutterBottom>
              Drop files here
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Release to upload files to this folder
            </Typography>
          </Box>
        </Box>
      )}
      {children}
    </Box>
  );
}

interface MainProps {
  search: string;
  onError: (error: Error) => void;
}

export default function Main({ search, onError }: MainProps) {
  const [cwd, setCwd] = React.useState("");
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [multiSelected, setMultiSelected] = useState<string[] | null>(null);
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);
  const [lastUploadKey, setLastUploadKey] = useState<string | null>(null);

  const transferQueue = useTransferQueue();
  const uploadEnqueue = useUploadEnqueue();

  const fetchFiles = useCallback(() => {
    fetchPath(cwd)
      .then((files) => {
        setFiles(files);
        setMultiSelected(null);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  }, [cwd, onError]);

  useEffect(() => setLoading(true), [cwd]);
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (!transferQueue.length) return;

    const lastFile = transferQueue[transferQueue.length - 1];
    if (["pending", "in-progress"].includes(lastFile.status))
      setLastUploadKey(lastFile.remoteKey);
    else if (lastUploadKey) {
      fetchFiles();
      setLastUploadKey(null);
    }
  }, [cwd, fetchFiles, lastUploadKey, transferQueue]);

  const filteredFiles = useMemo(
    () =>
      (search
        ? files.filter((file) =>
            file.key.toLowerCase().includes(search.toLowerCase())
          )
        : files
      ).sort((a, b) =>
        isDirectory(a) ? -1 : isDirectory(b) ? 1 : 0
      ),
    [files, search]
  );

  const handleMultiSelect = useCallback((key: string) => {
    setMultiSelected((multiSelected) => {
      if (multiSelected === null) {
        return [key];
      } else if (multiSelected.includes(key)) {
        const newSelected = multiSelected.filter((k) => k !== key);
        return newSelected.length ? newSelected : null;
      }
      return [...multiSelected, key];
    });
  }, []);

  return (
    <React.Fragment>
      {cwd && <PathBreadcrumb path={cwd} onCwdChange={setCwd} />}
      {loading ? (
        <Centered>
          <CircularProgress />
        </Centered>
      ) : (
        <DropZone
          onDrop={async (files) => {
            uploadEnqueue(
              ...Array.from(files).map((file) => ({ file, basedir: cwd }))
            );
          }}
        >
          <FileGrid
            files={filteredFiles}
            onCwdChange={(newCwd) => setCwd(newCwd)}
            multiSelected={multiSelected}
            onMultiSelect={handleMultiSelect}
            emptyMessage={
              <Centered>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 4,
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 400 }}>
                    {search ? 'No results found' : 'This folder is empty'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {search ? 'Try adjusting your search' : 'Drop files here or click the + button to get started'}
                  </Typography>
                </Box>
              </Centered>
            }
          />
        </DropZone>
      )}
      {multiSelected === null && (
        <UploadFab onClick={() => setShowUploadDrawer(true)} />
      )}
      <UploadDrawer
        open={showUploadDrawer}
        setOpen={setShowUploadDrawer}
        cwd={cwd}
        onUpload={fetchFiles}
      />
      <MultiSelectToolbar
        multiSelected={multiSelected}
        onClose={() => setMultiSelected(null)}
        onDownload={async () => {
          if (multiSelected?.length !== 1) return;
          try {
            const response = await authenticatedFetch(`/webdav/${encodeKey(multiSelected[0])}`);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = multiSelected[0].split("/").pop()!;
            a.click();
            URL.revokeObjectURL(url);
          } catch (error) {
            onError(error as Error);
          }
        }}
        onRename={async () => {
          if (multiSelected?.length !== 1) return;
          const newName = window.prompt("Rename to:");
          if (!newName) return;
          await copyPaste(multiSelected[0], cwd + newName, true);
          fetchFiles();
        }}
        onDelete={async () => {
          if (!multiSelected?.length) return;
          const filenames = multiSelected
            .map((key) => key.replace(/\/$/, "").split("/").pop())
            .join("\n");
          const confirmMessage = "Delete the following file(s) permanently?";
          if (!window.confirm(`${confirmMessage}\n${filenames}`)) return;
          for (const key of multiSelected)
            await authenticatedFetch(`/webdav/${encodeKey(key)}`, { method: "DELETE" });
          fetchFiles();
        }}
      />
    </React.Fragment>
  );
}
